import { clsx } from 'clsx';
import { useState, useEffect, useRef, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LogoFullColorImgSrc from '../../../../../assets/images/sendit-logo-full-color.svg';
import LogoFullWhiteColorImgSrc from '../../../../../assets/images/sendit-logo-full-white-color.svg';
import {
  ProviderCompletedRegistrationStepsEnum,
  ProviderOnboarding,
  OnboardingProviderService,
} from '../../../../../js/api';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { useCodeDefinitionsByCategories } from '../../../hooks/useCodeDefinitions';
import { APP_BG_COLOR } from '../../../utils/globalStyles';
import { BasicInfo, BasicInfoRef } from './BasicInfo';
import {
  getFirstIncompleteWizardStep,
  isProviderOnboardingComplete,
} from './onboarding-completion';
import { ProfessionalDetails, ProfessionalDetailsRef } from './ProfessionalDetails';
import { Rates, RatesRef } from './Rates';
import { StepNavigation, steps } from './StepNavigation';
import { transformFormData } from './transformers';
import { OnboardingFormData } from './types';

function applyOnboardingNavigationState(
  completedSteps: string[] | undefined,
  setCurrentStep: (step: number) => void,
  setIsRedirecting: (isRedirecting: boolean) => void
): boolean {
  const list = completedSteps ?? [];
  if (isProviderOnboardingComplete(list)) {
    setIsRedirecting(true);
    setTimeout(() => {
      window.location.href = '/app/provider/home';
    }, 500);
    return true;
  }

  setCurrentStep(getFirstIncompleteWizardStep(list));
  return false;
}

/** Keeps direct navigation within `steps` (1-based index). */
function clampProviderStep(step: number): number {
  return Math.min(Math.max(step, 1), steps.length);
}

export const ProviderOnboardingWrapper = () => {
  useDocumentTitle('Provider Onboarding');
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBasicInfoErrors, setShowBasicInfoErrors] = useState(false);
  const [providerData, setProviderData] = useState<ProviderOnboarding | null>(null);
  const { user, isLoading: isLoadingUser } = useCurrentUser();
  const providerId = user?.provider_id;
  const basicInfoRef = useRef<BasicInfoRef>(null);
  const ratesRef = useRef<RatesRef>(null);
  const professionalDetailsRef = useRef<ProfessionalDetailsRef>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Fetch all code definitions using the centralized manager
  const { data: codeDefinitionsByCategory } = useCodeDefinitionsByCategories([
    'PostalStateChoices',
    'ProviderRateFields',
    'WorkType',
    'TravelType',
    'LicenseStateChoices',
    'BoardCertificationType',
    'Degree',
    'Specialty',
    'CertificationType',
  ]);

  // Map to the existing apiData structure for compatibility
  const apiData = useMemo(
    () => ({
      postalStates: codeDefinitionsByCategory?.PostalStateChoices || [],
      rates: codeDefinitionsByCategory?.ProviderRateFields || [],
      workTypes: codeDefinitionsByCategory?.WorkType || [],
      travelTypes: codeDefinitionsByCategory?.TravelType || [],
      licenseStateOptions: codeDefinitionsByCategory?.LicenseStateChoices || [],
      boardCerts: codeDefinitionsByCategory?.BoardCertificationType || [],
      degrees: codeDefinitionsByCategory?.Degree || [],
      specialties: codeDefinitionsByCategory?.Specialty || [],
      lifeSupportCerts: codeDefinitionsByCategory?.CertificationType || [],
    }),
    [codeDefinitionsByCategory]
  );

  const codeDefinitions = useMemo(
    () => ({
      postalStateOptions: apiData.postalStates.map((opt) => ({
        value: opt.code,
        label: opt.title,
      })),
      providerRateOptions: apiData.rates.map((opt) => ({ value: opt.code, label: opt.title })),
      workTypeOptions: apiData.workTypes.map((opt) => ({ value: opt.code, label: opt.title })),
      travelTypeOptions: apiData.travelTypes.map((opt) => ({ value: opt.code, label: opt.title })),
      licenseStateOptions: apiData.licenseStateOptions.map((opt) => ({
        value: opt.code,
        label: opt.title,
      })),
      boardCertificationOptions: apiData.boardCerts.map((opt) => ({
        value: opt.code,
        label: opt.title,
      })),
      degreeOptions: apiData.degrees.map((opt) => ({ value: opt.code, label: opt.title })),
      specialtyOptions: apiData.specialties.map((opt) => ({ value: opt.code, label: opt.title })),
      lifeSupportCertificationOptions: apiData.lifeSupportCerts.map((opt) => ({
        value: opt.code,
        label: opt.title,
      })),
    }),
    [apiData]
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const initialFormData = useMemo(() => {
    if (!providerData || !apiData) return null;

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    const birthDate =
      providerData.birth_date && dateRegex.test(providerData.birth_date)
        ? providerData.birth_date
            .split('-')
            .slice(1)
            .concat(providerData.birth_date.split('-')[0])
            .join('/')
        : '';

    return {
      1: {
        firstName: providerData.first_name || '',
        lastName: providerData.last_name || '',
        dateOfBirth: birthDate,
        npiNumber: providerData.npi || '',
        streetAddress: providerData.street_address || '',
        city: providerData.city || '',
        state: providerData.state || '',
        zip: providerData.zip_code || '',
      },
      2: {
        shiftRateDay: providerData.shift_rate_day,
        shiftRateNight: providerData.shift_rate_night,
        shiftRateCall: providerData.shift_rate_call,
        shiftRate24h: providerData.shift_rate_24h,
        travelType: providerData.travel_type || '',
        workType: providerData.work_type || '',
      },
      3: {
        taxonomies: providerData.metadata?.nppes?.taxonomies || [],
        licensedStates:
          providerData.licenses?.map((license) => ({
            value: license.state,
            label:
              apiData.licenseStateOptions.find((opt) => opt.code === license.state)?.title ||
              license.state,
          })) || [],
        isResident: providerData.is_resident || false,
        estimatedGraduationDate: providerData.resident_graduation_on
          ? new Date(providerData.resident_graduation_on)
          : null,
        hasCompactLicense: providerData.has_imlc || false,
        degree:
          providerData.degree?.map((d) => ({
            value: d,
            label: apiData.degrees.find((opt) => opt.code === d)?.title || d,
          })) || [],
        specialties:
          providerData.specialties?.map((s) => ({
            value: s,
            label: apiData.specialties.find((opt) => opt.code === s)?.title || s,
          })) || [],
        boardCertifications:
          providerData.board_certifications?.map((bc) => ({
            value: bc.type,
            label: apiData.boardCerts.find((opt) => opt.code === bc.type)?.title || bc.type,
            boardEligible: bc.board_eligible,
          })) || [],
        lifeSupportCertifications:
          providerData.certifications?.map((c) => ({
            value: c.type,
            label: apiData.lifeSupportCerts.find((opt) => opt.code === c.type)?.title || c.type,
          })) || [],
      },
    };
  }, [providerData, apiData]);

  useEffect(() => {
    const fetchProviderData = async () => {
      if (!providerId) {
        setIsInitialLoading(false);
        return;
      }

      try {
        const providerData = await OnboardingProviderService.onboardingProviderRetrieve({
          id: providerId,
        });

        setProviderData(providerData);
        applyOnboardingNavigationState(
          providerData.completed_registration_steps,
          setCurrentStep,
          setIsRedirecting
        );
      } catch (error) {
        console.error('Error fetching provider data:', error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (!isLoadingUser) {
      fetchProviderData();
    }
  }, [providerId, isLoadingUser]);

  const saveFormData = async (formData: OnboardingFormData) => {
    if (!providerId) {
      console.error('No provider ID found');
      return;
    }

    try {
      setIsSubmitting(true);
      const transformedData = {
        ...transformFormData(formData),
        registration_step: steps[currentStep - 1].value as ProviderCompletedRegistrationStepsEnum,
      };
      const updatedData = await OnboardingProviderService.onboardingProviderPartialUpdate({
        id: providerId,
        requestBody: transformedData,
      });

      setProviderData(updatedData);
      return updatedData;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentStepFormRef = () => {
    if (currentStep === 1) return basicInfoRef.current;
    if (currentStep === 2) return ratesRef.current;
    return professionalDetailsRef.current;
  };

  const validateBeforePersist = (): boolean => {
    if (currentStep !== 1) return true;

    setShowBasicInfoErrors(true);
    if (!basicInfoRef.current?.hasFatalErrors) return true;

    const fatalErrors = Object.entries(basicInfoRef.current.validationMessages || {})
      .filter(([, message]) => message.colorClass.includes('danger'))
      .map(([, message]) => message.message)
      .join(', ');

    toast.error(
      <>
        Please fix the following errors:
        <br />
        {fatalErrors}
      </>
    );
    return false;
  };

  const persistCurrentStep = async (): Promise<ProviderOnboarding | null> => {
    const formData = getCurrentStepFormRef()?.formData;
    if (!formData) {
      toast.error('Unable to save this step. Please try again.');
      return null;
    }
    const saved = await saveFormData(formData);
    return saved ?? null;
  };

  /**
   * After a successful save: if `completed_registration_steps` reflects a full
   * wizard (same criteria the backend uses before `finalize_provider_registration`),
   * trigger redirect home. Finalization (rates, notifications, etc.) already runs
   * inside the onboarding PUT/PATCH handler — there is no separate complete endpoint.
   */
  const tryFinalizeAfterSave = (updated: ProviderOnboarding): boolean => {
    if (!isProviderOnboardingComplete(updated.completed_registration_steps)) {
      return false;
    }

    applyOnboardingNavigationState(
      updated.completed_registration_steps,
      setCurrentStep,
      setIsRedirecting
    );
    return true;
  };

  /**
   * Shared path: validate → persist current step → finalize + redirect if wizard complete.
   * Returns the updated provider only when the caller should change `currentStep` locally.
   */
  const validatePersistAndContinueLocally = async (): Promise<ProviderOnboarding | null> => {
    if (!validateBeforePersist()) return null;
    const updatedProvider = await persistCurrentStep();
    if (!updatedProvider) return null;
    if (tryFinalizeAfterSave(updatedProvider)) return null;
    return updatedProvider;
  };

  /** Step icons and Back: save, then go to the chosen step. */
  const handleDirectStepNavigation = async (step: number) => {
    const updated = await validatePersistAndContinueLocally();
    if (!updated) return;
    setCurrentStep(clampProviderStep(step));
  };

  /**
   * Save and Next: advance one step on non-final screens. On the last wizard step only, if
   * onboarding is still incomplete after save, jump to the first missing step in order.
   */
  const handleCalculatedStepNavigation = async () => {
    const updated = await validatePersistAndContinueLocally();
    if (!updated) return;

    if (
      currentStep === steps.length &&
      !isProviderOnboardingComplete(updated.completed_registration_steps)
    ) {
      setCurrentStep(getFirstIncompleteWizardStep(updated.completed_registration_steps));
      return;
    }

    setCurrentStep(clampProviderStep(currentStep + 1));
  };

  const handleExit = () => {
    window.location.href = '/app/provider/home';
  };

  const currentStepInfo = steps[currentStep - 1];

  return (
    <div className={clsx('tw:flex tw:flex-col tw:min-h-screen', APP_BG_COLOR)}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="tw:flex tw:justify-center tw:m-4">
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div onClick={handleExit} className="tw:cursor-pointer">
          <img src={LogoFullColorImgSrc} alt="Sendit logo" className="tw:h-16 tw:dark:hidden" />
          <img
            src={LogoFullWhiteColorImgSrc}
            alt="Sendit logo"
            className="tw:h-16 tw:hidden tw:dark:block"
          />
        </div>
      </div>
      <div className="tw:max-w-5xl tw:mx-auto tw:w-full tw:flex-grow">
        <div className="tw:lg:flex tw:lg:gap-8 tw:lg:p-4">
          <StepNavigation
            currentStep={currentStep}
            triggerFormAction={(step) => void handleDirectStepNavigation(step)}
            completedSteps={providerData?.completed_registration_steps || []}
          />
          <div className="tw:lg:flex-1 tw:lg:m-0 tw:h-full">
            {isInitialLoading ? (
              <div className="tw:flex tw:items-center tw:justify-center tw:h-64">
                <div className="tw:text-gray-500">Loading...</div>
              </div>
            ) : (
              <div className="tw:mt-0 tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:h-full">
                <div className="tw:max-w-2xl tw:mx-auto">
                  <div>
                    {currentStep === 1 && initialFormData && (
                      <BasicInfo
                        ref={basicInfoRef}
                        stepInfo={currentStepInfo}
                        initialData={initialFormData[1]}
                        registrationSteps={providerData?.metadata?.registration_steps}
                        stateOptions={codeDefinitions?.postalStateOptions || []}
                        nppesData={providerData?.metadata?.nppes}
                        showFatalErrors={showBasicInfoErrors}
                      />
                    )}
                  </div>
                  <div>
                    {currentStep === 2 && (
                      <Rates
                        ref={ratesRef}
                        stepInfo={currentStepInfo}
                        initialData={initialFormData?.[2]}
                        registrationSteps={providerData?.metadata?.registration_steps}
                        providerRateOptions={codeDefinitions?.providerRateOptions || []}
                        workTypeOptions={codeDefinitions?.workTypeOptions || []}
                        travelTypeOptions={codeDefinitions?.travelTypeOptions || []}
                      />
                    )}
                  </div>
                  <div>
                    {currentStep === 3 && (
                      <ProfessionalDetails
                        ref={professionalDetailsRef}
                        stepInfo={currentStepInfo}
                        initialData={initialFormData?.[3]}
                        registrationSteps={providerData?.metadata?.registration_steps}
                        licenseStateOptions={codeDefinitions?.licenseStateOptions || []}
                        boardCertificationOptions={codeDefinitions?.boardCertificationOptions || []}
                        degreeOptions={codeDefinitions?.degreeOptions || []}
                        specialtyOptions={codeDefinitions?.specialtyOptions || []}
                        lifeSupportCertificationOptions={
                          codeDefinitions?.lifeSupportCertificationOptions || []
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="tw:flex tw:justify-between tw:mx-4 tw:mb-4 tw:max-w-2xl tw:mx-auto">
              <Button
                plain
                tabIndex={-1}
                className="tw:bg-white tw:border-1 tw:border-zinc-200 tw:hover:bg-gray-50 tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:dark:hover:!bg-zinc-500"
                onClick={handleExit}
                disabled={isSubmitting}
              >
                Finish Later
              </Button>
              <div>
                {currentStep > 1 && (
                  <Button
                    plain
                    tabIndex={-1}
                    className="tw:bg-white tw:border-1 tw:border-zinc-200 tw:hover:bg-gray-50 tw:dark:bg-zinc-600 tw:dark:border-zinc-500 tw:dark:hover:!bg-zinc-500"
                    onClick={() => void handleDirectStepNavigation(currentStep - 1)}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                )}
                <Button
                  plain
                  className="tw:!ml-4 tw:bg-brand-green-1 tw:!text-white tw:hover:!bg-brand-green-0 tw:dark:hover:!bg-brand-green-0"
                  onClick={() => void handleCalculatedStepNavigation()}
                  disabled={isSubmitting}
                >
                  Save and Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isRedirecting && (
        <div className="tw:fixed tw:inset-0 tw:bg-black/50 tw:flex tw:items-center tw:justify-center tw:z-50">
          <div className="tw:bg-white tw:dark:bg-zinc-800 tw:p-6 tw:rounded-lg tw:shadow-xl tw:text-center">
            <div className="tw:animate-spin tw:rounded-full tw:h-12 tw:w-12 tw:border-4 tw:border-zinc-300 tw:border-t-brand-green-1 tw:mx-auto tw:mb-4" />
            <p className="tw:text-zinc-700 tw:dark:text-zinc-200">Redirecting to your profile...</p>
          </div>
        </div>
      )}
    </div>
  );
};
